import type { Point2 } from "@town/contracts";
import { cameraInsets, clampCameraFrame, frameCenter, stepCameraZoom, type CameraBounds } from "./cameraViewport";

/**
 * The office projection shared by both renderers. A fixed orthographic camera looks north at
 * `atan2(32, 20)`, and an anisotropic frustum makes one world unit exactly `d` texel columns in x,
 * `d` texel rows in z and `0.625·d` rows in y. At d = 32 a point lands on column `32x` and row
 * `32z − 20y`, so screen space is linear in (x, z − 0.625y) with square texels.
 */
export const CAMERA_ELEVATION = Math.atan2(32, 20);
/** Rows per unit of height relative to rows per unit of depth: cot(elevation), exactly 0.625. */
export const HEIGHT_ROWS_PER_UNIT = 20 / 32;
export const CAMERA_DISTANCE = 50;
export const CAMERA_NEAR = 1;
export const CAMERA_FAR = 100;
/** One spare texel on every side of the low-res image absorbs the composite offset. */
export const LOW_RES_MARGIN = 1;

export type TexelDensity = 32 | 16;
export type PixelLevel = Readonly<{ density: TexelDensity; pixelSize: number }>;

/** `{32, 1..8}` and `{16, 1..4}`, ordered by on-screen tile size. Ties put the overview level first. */
export const PIXEL_LEVELS: readonly PixelLevel[] = [
  ...[1, 2, 3, 4, 5, 6, 7, 8].map((pixelSize) => ({ density: 32 as const, pixelSize })),
  ...[1, 2, 3, 4].map((pixelSize) => ({ density: 16 as const, pixelSize })),
].sort((a, b) => a.density * a.pixelSize - b.density * b.pixelSize || a.density - b.density);

/** Widest and closest zoom numbers a zoom list offers. */
export type ZoomLimits = Readonly<{ min: number; max: number }>;

/**
 * The widest and closest zoom numbers of every level. A budget can reduce the
 * canvas and remove the widest numbers, so the zoom controls read the
 * camera's zoomLimits instead.
 */
export const ZOOM_SCALE_RANGE: ZoomLimits = {
  min: Math.min(...PIXEL_LEVELS.map(({ density, pixelSize }) => (density * pixelSize) / 32)),
  max: Math.max(...PIXEL_LEVELS.map(({ density, pixelSize }) => (density * pixelSize) / 32)),
} as const;

/** The zoom label: "2x" means one tile is 64 device pixels. */
export function zoomLabel(scale: number): string {
  return `${Math.round(scale * 100) / 100}x`;
}

/**
 * A zoom choice after the budget is applied. `renderScale` (k) is the integer CSS upscale of a
 * reduced canvas; `scale` is device pixels per 1/32 of a tile (so one tile is `32·scale` device
 * pixels), which is also the zoom number the UI shows.
 */
export type ZoomEntry = Readonly<{ scale: number; density: TexelDensity; pixelSize: number; renderScale: number }>;

/** Rendering limits for a performance mode: low-res texels, and the smallest texel in device pixels. */
export type PixelBudget = Readonly<{ maxTexels: number; minPixelSize: number }>;

/** Default: the closest density-32 level that still shows this many tiles across the shorter axis. */
export const DEFAULT_MIN_VISIBLE_TILES = 11;

export type DeviceSize = Readonly<{ cssWidth: number; cssHeight: number; deviceWidth: number; deviceHeight: number; dpr: number }>;

/** Texel centres at d = 16 sit at (4k + 3)/64, never on a 1/32 texture edge. */
export function texelPhase(density: TexelDensity): number {
  return density === 16 ? 1 / 64 : 0;
}

export function snapToTexel(value: number, density: TexelDensity): number {
  return Math.round(value * density) / density;
}

/** Whole texels in x and z and whole rows in y (0.05 units at d = 32). */
export function snapRoot<T extends Readonly<{ x: number; y: number; z: number }>>(position: T, density: TexelDensity): { x: number; y: number; z: number } {
  const rows = HEIGHT_ROWS_PER_UNIT * density;
  return { x: snapToTexel(position.x, density), y: Math.round(position.y * rows) / rows, z: snapToTexel(position.z, density) };
}

/** Low-res extent: even, with one margin texel on each side. */
export function lowResSize(bufferPixels: number, pixelSize: number): number {
  return 2 * Math.ceil((Math.ceil(bufferPixels / pixelSize) + 2 * LOW_RES_MARGIN) / 2);
}

/** Smallest integer canvas reduction that satisfies the mode's texel and texel-size limits. */
export function renderScaleFor(level: PixelLevel, deviceWidth: number, deviceHeight: number, budget: PixelBudget): number {
  let scale = Math.max(1, Math.ceil(budget.minPixelSize / level.pixelSize));
  // Visible texels; the two margin rows and columns are not part of the budget.
  const texels = (k: number) => Math.ceil(deviceWidth / k / level.pixelSize) * Math.ceil(deviceHeight / k / level.pixelSize);
  while (texels(scale) > budget.maxTexels && scale < 64) scale++;
  return scale;
}

/** Distinct on-screen tile sizes, preferring no canvas reduction, then the denser level. */
export function zoomEntries(deviceWidth: number, deviceHeight: number, budget: PixelBudget): ZoomEntry[] {
  const byScale = new Map<number, ZoomEntry>();
  for (const level of PIXEL_LEVELS) {
    const renderScale = renderScaleFor(level, deviceWidth, deviceHeight, budget);
    const entry: ZoomEntry = { scale: (level.density * level.pixelSize * renderScale) / 32, ...level, renderScale };
    const current = byScale.get(entry.scale);
    if (!current || entry.renderScale < current.renderScale || (entry.renderScale === current.renderScale && entry.density > current.density)) {
      byScale.set(entry.scale, entry);
    }
  }
  return [...byScale.values()].sort((a, b) => a.scale - b.scale);
}

/** The listed entry nearest a requested zoom number, compared on a log scale. */
export function nearestZoomEntry(entries: readonly ZoomEntry[], scale: number): ZoomEntry {
  let best = entries[0]!;
  for (const entry of entries) {
    if (Math.abs(Math.log(entry.scale / scale)) < Math.abs(Math.log(best.scale / scale)) - 1e-9) best = entry;
  }
  return best;
}

/** Tiles left between the HUD insets, in device pixels. */
function visibleDevicePixels(size: DeviceSize): { width: number; height: number } {
  const inset = cameraInsets(size.cssWidth, size.cssHeight);
  const ratioX = size.deviceWidth / Math.max(1, size.cssWidth);
  const ratioY = size.deviceHeight / Math.max(1, size.cssHeight);
  return {
    width: Math.max(1, (size.cssWidth - inset.left - inset.right) * ratioX),
    height: Math.max(1, (size.cssHeight - inset.top - inset.bottom) * ratioY),
  };
}

export function defaultZoomEntry(entries: readonly ZoomEntry[], size: DeviceSize): ZoomEntry {
  const visible = visibleDevicePixels(size);
  const dense = entries.filter((entry) => entry.density === 32);
  let chosen = dense[0] ?? entries[0]!;
  for (const entry of dense) {
    if (Math.min(visible.width, visible.height) / (32 * entry.scale) >= DEFAULT_MIN_VISIBLE_TILES) chosen = entry;
  }
  return chosen;
}

/**
 * The closest entry that shows the whole bounds between the HUD insets. If none fits, the closest
 * density-16 entry that fits the width (the user pans vertically), else the widest entry.
 */
export function fitZoomEntry(entries: readonly ZoomEntry[], size: DeviceSize, bounds: CameraBounds): ZoomEntry {
  const visible = visibleDevicePixels(size);
  const width = bounds.maxX - bounds.minX;
  const height = bounds.maxY - bounds.minY;
  const tiles = (entry: ZoomEntry) => ({ x: visible.width / (32 * entry.scale), y: visible.height / (32 * entry.scale) });
  const fitting = entries.filter((entry) => tiles(entry).x >= width && tiles(entry).y >= height);
  if (fitting.length) return fitting[fitting.length - 1]!;
  const overview = entries.filter((entry) => entry.density === 16 && tiles(entry).x >= width);
  return overview[overview.length - 1] ?? entries[0]!;
}

/** CSS pixels per world unit for an entry. */
export function cssPixelsPerUnit(entry: Pick<ZoomEntry, "scale">, dpr: number): number {
  return (32 * entry.scale) / dpr;
}

/** Clamps a floor focus so the view stays within the bounds, with overscroll for the HUD. */
export function clampFocusToBounds(focus: Point2, bounds: CameraBounds, entry: Pick<ZoomEntry, "scale">, size: DeviceSize): Point2 {
  const perUnit = cssPixelsPerUnit(entry, size.dpr);
  const clamped = clampCameraFrame({ x: focus.x, y: focus.z }, bounds, size.cssWidth / perUnit, size.cssHeight / perUnit, size.cssWidth, size.cssHeight);
  return { x: clamped.x, z: clamped.y };
}

/** The focus that centres the bounds between the HUD insets at an entry's scale. */
export function fitFocus(bounds: CameraBounds, entry: Pick<ZoomEntry, "scale">, size: DeviceSize): Point2 {
  const center = frameCenter(bounds, cssPixelsPerUnit(entry, size.dpr), size.cssWidth, size.cssHeight);
  return clampFocusToBounds({ x: center.x, z: center.y }, bounds, entry, size);
}

/**
 * Snapped camera focus and the composite offset for one axis.
 * Follow mode keeps the camera a whole number of texels from the snapped player root, so the player
 * never moves on screen while the world scrolls in whole texels. Free mode (drags, easing, a fixed
 * fit view, an axis held at the map edge) snaps the focus and carries the remainder as a whole
 * device-pixel offset of the upscale.
 */
export function snapCameraAxis(focus: number, anchor: number | null, entry: Pick<ZoomEntry, "density" | "pixelSize">): { camera: number; offset: number } {
  const { density, pixelSize } = entry;
  const phase = texelPhase(density);
  if (anchor !== null) {
    return { camera: snapToTexel(anchor, density) + Math.round((focus - anchor) * density) / density + phase, offset: 0 };
  }
  const camera = Math.round((focus - phase) * density) / density + phase;
  return { camera, offset: Math.round((focus - camera) * density * pixelSize) };
}

export type PixelCameraFrame = Readonly<{ focus: Point2; offset: Readonly<{ x: number; y: number }> }>;

/** Pass the anchor per axis to follow it, or null on an axis that moves freely. */
export function pixelCameraFrame(focus: Point2, anchor: Readonly<{ x: number | null; z: number | null }>, entry: Pick<ZoomEntry, "density" | "pixelSize">): PixelCameraFrame {
  const x = snapCameraAxis(focus.x, anchor.x, entry);
  const z = snapCameraAxis(focus.z, anchor.z, entry);
  return { focus: { x: x.camera, z: z.camera }, offset: { x: x.offset, y: z.offset } };
}

export type PixelViewportInit = Readonly<{
  size: DeviceSize;
  zoom: Pick<ZoomEntry, "density" | "pixelSize" | "renderScale">;
  /** The snapped camera focus on the floor, from `pixelCameraFrame`. */
  focus: Point2;
  /** Composite offset in canvas buffer pixels. */
  offset?: Readonly<{ x: number; y: number }>;
}>;

/**
 * Maps between world, low-res texels, canvas buffer pixels and CSS client pixels for one frame.
 * Raycasts, placement, hit boxes, labels, DOM anchors and the Canvas 2D renderer all use it, so
 * input and overlays line up with the integer upscale of the low-res image.
 *
 * Buffer pixel (bx, by), counted from the top left, shows texel
 * `floor((bx + offPx) / p) + margin` (and likewise for rows, counted from the top).
 */
export class PixelViewport {
  readonly density: TexelDensity;
  readonly pixelSize: number;
  readonly renderScale: number;
  readonly dpr: number;
  readonly cssWidth: number;
  readonly cssHeight: number;
  readonly bufferWidth: number;
  readonly bufferHeight: number;
  readonly cols: number;
  readonly rows: number;
  readonly margin = LOW_RES_MARGIN;
  readonly focusX: number;
  readonly focusZ: number;
  readonly offPx: number;
  readonly offPy: number;

  constructor({ size, zoom, focus, offset = { x: 0, y: 0 } }: PixelViewportInit) {
    this.density = zoom.density;
    this.pixelSize = zoom.pixelSize;
    this.renderScale = zoom.renderScale;
    this.dpr = size.dpr;
    this.cssWidth = size.cssWidth;
    this.cssHeight = size.cssHeight;
    this.bufferWidth = Math.max(1, Math.ceil(size.deviceWidth / zoom.renderScale));
    this.bufferHeight = Math.max(1, Math.ceil(size.deviceHeight / zoom.renderScale));
    this.cols = lowResSize(this.bufferWidth, zoom.pixelSize);
    this.rows = lowResSize(this.bufferHeight, zoom.pixelSize);
    this.focusX = focus.x;
    this.focusZ = focus.z;
    this.offPx = offset.x;
    this.offPy = offset.y;
  }

  /** The canvas CSS size that shows every buffer pixel at exactly `renderScale` device pixels. */
  get canvasCssWidth(): number { return (this.bufferWidth * this.renderScale) / this.dpr; }
  get canvasCssHeight(): number { return (this.bufferHeight * this.renderScale) / this.dpr; }

  /** Orthographic frustum half-extents in camera units for the low-res target. */
  frustum(): { left: number; right: number; top: number; bottom: number } {
    const halfWidth = this.cols / (2 * this.density);
    const halfHeight = (this.rows * Math.sin(CAMERA_ELEVATION)) / (2 * this.density);
    return { left: -halfWidth, right: halfWidth, top: halfHeight, bottom: -halfHeight };
  }

  /** Continuous low-res coordinates, columns from the left and rows from the top. */
  worldToTexel(x: number, y: number, z: number): { u: number; v: number } {
    return {
      u: (x - this.focusX) * this.density + this.cols / 2,
      v: (z - this.focusZ - HEIGHT_ROWS_PER_UNIT * y) * this.density + this.rows / 2,
    };
  }

  worldToBuffer(x: number, y: number, z: number): { x: number; y: number } {
    const { u, v } = this.worldToTexel(x, y, z);
    return { x: (u - this.margin) * this.pixelSize - this.offPx, y: (v - this.margin) * this.pixelSize - this.offPy };
  }

  /** CSS pixels from the canvas's top-left corner. */
  worldToClient(x: number, y: number, z: number): { x: number; y: number } {
    const buffer = this.worldToBuffer(x, y, z);
    const ratio = this.renderScale / this.dpr;
    return { x: buffer.x * ratio, y: buffer.y * ratio };
  }

  clientToTexel(clientX: number, clientY: number): { u: number; v: number } {
    const ratio = this.dpr / this.renderScale;
    return {
      u: (clientX * ratio + this.offPx) / this.pixelSize + this.margin,
      v: (clientY * ratio + this.offPy) / this.pixelSize + this.margin,
    };
  }

  /** Normalized device coordinates of the low-res camera, for raycasting. */
  clientToNdc(clientX: number, clientY: number): { x: number; y: number } {
    const { u, v } = this.clientToTexel(clientX, clientY);
    return { x: (2 * u) / this.cols - 1, y: 1 - (2 * v) / this.rows };
  }

  /** The point under the pointer on the horizontal plane at `planeY`. */
  clientToWorld(clientX: number, clientY: number, planeY = 0): Point2 {
    const { u, v } = this.clientToTexel(clientX, clientY);
    return {
      x: this.focusX + (u - this.cols / 2) / this.density,
      z: this.focusZ + (v - this.rows / 2) / this.density + HEIGHT_ROWS_PER_UNIT * planeY,
    };
  }

  /** The integer low-res texel a canvas buffer pixel shows; the upscale shader mirrors this. */
  compositeTexel(bufferX: number, bufferY: number): { col: number; row: number } {
    return {
      col: Math.floor((bufferX + this.offPx) / this.pixelSize) + this.margin,
      row: Math.floor((bufferY + this.offPy) / this.pixelSize) + this.margin,
    };
  }

  /** Writes the screen-plane extent without allocating, for per-frame scene updates. */
  visibleBoundsInto(target: { minX: number; maxX: number; minY: number; maxY: number }): void {
    const u0 = this.offPx / this.pixelSize + this.margin;
    const v0 = this.offPy / this.pixelSize + this.margin;
    const u1 = this.bufferWidth / this.pixelSize + u0;
    const v1 = this.bufferHeight / this.pixelSize + v0;
    target.minX = this.focusX + (u0 - this.cols / 2) / this.density;
    target.maxX = this.focusX + (u1 - this.cols / 2) / this.density;
    target.minY = this.focusZ + (v0 - this.rows / 2) / this.density;
    target.maxY = this.focusZ + (v1 - this.rows / 2) / this.density;
  }

  /** Screen-plane (x, z − 0.625y) extent of the canvas in world units. */
  visibleBounds(): CameraBounds {
    const bounds = { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    this.visibleBoundsInto(bounds);
    return bounds;
  }
}

const SETTLED_UNITS = 0.01;

/**
 * Camera framing shared by both office renderers: the zoom list for the current screen and budget,
 * the default and fit rules, easing, drag panning, and follow versus free snapping per axis.
 * Positions are world units on the floor; bounds are screen-plane (x, z − 0.625y) units.
 */
export class PixelCamera {
  readonly bounds: CameraBounds;
  #size: DeviceSize = { cssWidth: 1, cssHeight: 1, deviceWidth: 1, deviceHeight: 1, dpr: 1 };
  #entries: ZoomEntry[];
  #limits: ZoomLimits;
  #zoom: ZoomEntry;
  #zoomChosen = false;
  #fitted = false;
  #zoomBeforeFit: number | null = null;
  #focus: Point2;
  #pan = { x: 0, z: 0 };
  /**
   * After a recenter the focus eases toward the anchor with a composite offset, until it settles
   * or the anchor starts moving; a walking player is then followed in whole texels.
   */
  #easing = false;
  #moving = false;
  #lastAnchor: Point2 | null = null;
  #viewport: PixelViewport;

  constructor(bounds: CameraBounds, focus: Point2) {
    this.bounds = bounds;
    this.#focus = { ...focus };
    this.#entries = zoomEntries(1, 1, { maxTexels: 1, minPixelSize: 1 });
    this.#limits = this.#entryLimits();
    this.#zoom = defaultZoomEntry(this.#entries, this.#size);
    this.#viewport = this.#frame(null);
  }

  get viewport(): PixelViewport { return this.#viewport; }
  get size(): DeviceSize { return this.#size; }
  get zoom(): number { return this.#zoom.scale; }
  get zoomEntry(): ZoomEntry { return this.#zoom; }
  get zoomScales(): number[] { return this.#entries.map((entry) => entry.scale); }
  /** The zoom numbers this screen and budget allow. The object changes only when the limits do. */
  get zoomLimits(): ZoomLimits { return this.#limits; }
  get fitted(): boolean { return this.#fitted; }
  /** True while the focus is still easing toward its target. */
  get moving(): boolean { return this.#moving; }
  get focus(): Point2 { return this.#focus; }

  /** Applies a new canvas size or budget. Returns true when the zoom number changed. */
  resize(size: DeviceSize, budget: PixelBudget, anchor: Point2): boolean {
    const previous = this.#zoom.scale;
    this.#size = size;
    this.#entries = zoomEntries(size.deviceWidth, size.deviceHeight, budget);
    const limits = this.#entryLimits();
    if (limits.min !== this.#limits.min || limits.max !== this.#limits.max) this.#limits = limits;
    if (this.#fitted) this.#zoom = fitZoomEntry(this.#entries, size, this.bounds);
    else this.#zoom = this.#zoomChosen ? nearestZoomEntry(this.#entries, previous) : defaultZoomEntry(this.#entries, size);
    if (this.#fitted) this.#focus = this.#fitFocus();
    this.#viewport = this.#frame(this.#fitted || this.#easing ? null : anchor);
    return this.#zoom.scale !== previous;
  }

  #entryLimits(): ZoomLimits {
    return { min: this.#entries[0]!.scale, max: this.#entries[this.#entries.length - 1]!.scale };
  }

  /** Chooses the listed zoom nearest `scale`. Returns true when it changed. */
  setZoom(scale: number): boolean {
    this.#fitted = false;
    this.#zoomBeforeFit = null;
    this.#zoomChosen = true;
    const next = nearestZoomEntry(this.#entries, scale);
    const changed = next.scale !== this.#zoom.scale;
    this.#zoom = next;
    return changed;
  }

  /** Positive steps zoom in. */
  zoomBy(direction: 1 | -1): boolean {
    return this.setZoom(stepCameraZoom(this.#zoom.scale, this.zoomScales, direction));
  }

  fitOffice(anchor: Point2): boolean {
    const previous = this.#zoomBeforeFit ?? this.#zoom.scale;
    const changed = this.setZoom(fitZoomEntry(this.#entries, this.#size, this.bounds).scale);
    this.#zoomBeforeFit = previous;
    this.#fitted = true;
    this.#focus = this.#fitFocus();
    this.#pan = { x: this.#focus.x - anchor.x, z: this.#focus.z - anchor.z };
    this.#viewport = this.#frame(null);
    return changed;
  }

  /** Returns to following the anchor at the zoom used before a fit. Returns true when the zoom changed. */
  recenter(): boolean {
    const previous = this.#zoomBeforeFit;
    const changed = previous !== null && this.setZoom(previous);
    this.#fitted = false;
    this.#pan = { x: 0, z: 0 };
    this.#easing = true;
    this.#moving = true;
    return changed;
  }

  /**
   * Forgets any pan, so the next update follows the anchor itself. A clamp at the campus edge leaves a pan behind,
   * which would keep the view that far from an anchor that starts outside the campus.
   */
  clearPan(): void {
    this.#pan = { x: 0, z: 0 };
  }

  /** Drag panning in CSS pixels; the focus follows the pointer exactly. */
  panBy(deltaX: number, deltaY: number): void {
    this.#fitted = false;
    const perUnit = cssPixelsPerUnit(this.#zoom, this.#size.dpr);
    this.#pan = { x: this.#pan.x - deltaX / perUnit, z: this.#pan.z - deltaY / perUnit };
  }

  /** Advances easing and returns this frame's viewport. */
  update(anchor: Point2, deltaSeconds: number, dragging: boolean): PixelViewport {
    let desired: Point2;
    let clamped = { x: false, z: false };
    if (this.#fitted) desired = this.#fitFocus();
    else {
      const requested = { x: anchor.x + this.#pan.x, z: anchor.z + this.#pan.z };
      desired = this.#clamp(requested);
      clamped = { x: Math.abs(desired.x - requested.x) > 1e-9, z: Math.abs(desired.z - requested.z) > 1e-9 };
    }
    this.#pan = { x: desired.x - anchor.x, z: desired.z - anchor.z };
    const alpha = dragging ? 1 : 1 - Math.exp(-deltaSeconds * 6);
    const distance = Math.abs(desired.x - this.#focus.x) + Math.abs(desired.z - this.#focus.z);
    this.#moving = distance > SETTLED_UNITS;
    this.#focus = { x: this.#focus.x + (desired.x - this.#focus.x) * alpha, z: this.#focus.z + (desired.z - this.#focus.z) * alpha };
    const remaining = Math.max(Math.abs(desired.x - this.#focus.x), Math.abs(desired.z - this.#focus.z));
    const anchorMoved = this.#lastAnchor !== null && (this.#lastAnchor.x !== anchor.x || this.#lastAnchor.z !== anchor.z);
    this.#lastAnchor = { ...anchor };
    if (this.#easing && (anchorMoved || remaining * this.#zoom.density < 0.5)) this.#easing = false;
    const free = dragging || this.#fitted || this.#easing;
    this.#viewport = this.#frame({ x: free || clamped.x ? null : anchor.x, z: free || clamped.z ? null : anchor.z });
    return this.#viewport;
  }

  /** Centres on a new anchor without easing, for example when the local person changes. */
  jumpTo(anchor: Point2): void {
    this.#pan = { x: 0, z: 0 };
    this.#fitted = false;
    this.#zoomBeforeFit = null;
    this.#focus = { ...anchor };
    this.#easing = false;
    this.#lastAnchor = null;
    this.#viewport = this.#frame(anchor);
  }

  #clamp(point: Point2): Point2 {
    return clampFocusToBounds(point, this.bounds, this.#zoom, this.#size);
  }

  #fitFocus(): Point2 {
    return fitFocus(this.bounds, this.#zoom, this.#size);
  }

  #frame(anchor: Readonly<{ x: number | null; z: number | null }> | null): PixelViewport {
    const frame = pixelCameraFrame(this.#focus, anchor ?? { x: null, z: null }, this.#zoom);
    return new PixelViewport({ size: this.#size, zoom: this.#zoom, focus: frame.focus, offset: frame.offset });
  }
}

function currentPixelRatio(): number {
  return (typeof window === "undefined" ? 1 : window.devicePixelRatio) || 1;
}

/**
 * Snapping moves a device box by under one device pixel from the CSS box times the ratio. Chromium
 * under an emulated device scale factor (Playwright, DevTools) reports the CSS box in its place,
 * which would draw the office at 1/dpr of the stage, so a box further off than this is not trusted.
 */
function isDeviceBoxPlausible(box: ResizeObserverSize, css: Readonly<{ width: number; height: number }>, ratio: number): boolean {
  const slack = ratio + 1;
  return Math.abs(box.inlineSize - css.width * ratio) <= slack && Math.abs(box.blockSize - css.height * ratio) <= slack;
}

/**
 * Reports the element's exact device-pixel size. Uses `devicePixelContentBoxSize` where the
 * browser provides it and it agrees with the CSS size, otherwise rounds the CSS size times the
 * device pixel ratio, and listens for ratio changes that happen without a resize, such as moving a
 * window between monitors.
 */
export function observeDeviceSize(element: Element, onChange: (size: DeviceSize) => void): () => void {
  let dpr = 1;
  let media: MediaQueryList | null = null;
  let device: { width: number; height: number } | null = null;
  let content: { width: number; height: number } | null = null;
  const report = () => {
    const box = content ?? element.getBoundingClientRect();
    const cssWidth = Math.max(1, box.width);
    const cssHeight = Math.max(1, box.height);
    onChange({
      cssWidth, cssHeight, dpr,
      deviceWidth: Math.max(1, device?.width ?? Math.round(cssWidth * dpr)),
      deviceHeight: Math.max(1, device?.height ?? Math.round(cssHeight * dpr)),
    });
  };
  const observer = new ResizeObserver((entries) => {
    const entry = entries?.[0];
    const box = entry?.devicePixelContentBoxSize?.[0];
    content = entry?.contentRect ? { width: entry.contentRect.width, height: entry.contentRect.height } : null;
    const css = content ?? element.getBoundingClientRect();
    device = box && isDeviceBoxPlausible(box, css, currentPixelRatio()) ? { width: box.inlineSize, height: box.blockSize } : null;
    report();
  });
  const observe = () => {
    try { observer.observe(element, { box: "device-pixel-content-box" }); }
    catch { observer.observe(element); }
  };
  observe();
  const watchRatio = () => {
    media?.removeEventListener("change", ratioChanged);
    dpr = currentPixelRatio();
    media = typeof matchMedia === "function" ? matchMedia(`(resolution: ${dpr}dppx)`) : null;
    media?.addEventListener("change", ratioChanged);
  };
  function ratioChanged() {
    watchRatio();
    content = null;
    report();
    observer.unobserve(element);
    observe();
  }
  watchRatio();
  report();
  return () => {
    observer.disconnect();
    media?.removeEventListener("change", ratioChanged);
    media = null;
  };
}
